/**
 * Service Scheduler - Custom Date/Time Picker
 *
 * Replaces Ninja Forms date/time field with a custom solution
 * that respects business hours per day of week.
 *
 * Business Hours:
 * - Monday: 7:30am - 4:30pm
 * - Tuesday-Friday: 7:30am - 8:00pm
 * - Saturday: 8:00am - 2:00pm
 * - Sunday: Closed
 *
 * Usage: Add class "service-scheduler-datetime" to the Ninja Forms date field container
 */
(function() {
    'use strict';

    // Business hours configuration
    const SCHEDULE = {
        0: null, // Sunday - Closed
        1: { open: { h: 7, m: 30 }, close: { h: 16, m: 30 } }, // Monday
        2: { open: { h: 7, m: 30 }, close: { h: 20, m: 0 } },  // Tuesday
        3: { open: { h: 7, m: 30 }, close: { h: 20, m: 0 } },  // Wednesday
        4: { open: { h: 7, m: 30 }, close: { h: 20, m: 0 } },  // Thursday
        5: { open: { h: 7, m: 30 }, close: { h: 20, m: 0 } },  // Friday
        6: { open: { h: 8, m: 0 }, close: { h: 14, m: 0 } },   // Saturday
    };

    // Generate time slots for a given day
    function getTimeSlots(dayOfWeek) {
        const schedule = SCHEDULE[dayOfWeek];
        if (!schedule) return [];

        const slots = [];
        let hour = schedule.open.h;
        let min = schedule.open.m;
        const closeH = schedule.close.h;
        const closeM = schedule.close.m;

        while (hour < closeH || (hour === closeH && min <= closeM)) {
            const period = hour >= 12 ? 'PM' : 'AM';
            const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
            const displayMin = min.toString().padStart(2, '0');
            const value = `${displayHour}:${displayMin} ${period}`;

            slots.push({
                value: value,
                label: value
            });

            min += 30;
            if (min >= 60) {
                min = 0;
                hour++;
            }
        }

        return slots;
    }

    // Check if a date is available (not Sunday)
    function isDateDisabled(date) {
        return SCHEDULE[date.getDay()] === null;
    }

    // Format date as MM/DD/YYYY
    function formatDate(date) {
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    }

    // Load Flatpickr
    function loadFlatpickr(callback) {
        if (window.flatpickr) {
            callback();
            return;
        }

        // Load CSS
        if (!document.querySelector('link[href*="flatpickr"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://cdn.jsdelivr.net/npm/flatpickr/dist/flatpickr.min.css';
            document.head.appendChild(link);
        }

        // Load JS
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/flatpickr';
        script.onload = callback;
        document.head.appendChild(script);
    }

    // Create custom UI
    function createSchedulerUI(container, originalInput) {
        // Hide all Ninja Forms native date/time elements
        const nfElements = container.querySelectorAll('.nf-field-element > *');
        nfElements.forEach(el => {
            if (!el.classList.contains('service-scheduler-wrapper')) {
                el.style.display = 'none';
            }
        });

        // Also hide pikaday and native time selects
        const pikaday = container.querySelector('.pikaday__container, .pika-single');
        if (pikaday) pikaday.style.display = 'none';

        const timeSelects = container.querySelectorAll('select');
        timeSelects.forEach(sel => sel.style.display = 'none');

        // Create wrapper
        const wrapper = document.createElement('div');
        wrapper.className = 'service-scheduler-wrapper';
        wrapper.innerHTML = `
            <div class="service-scheduler-container">
                <div class="service-scheduler-row">
                    <div class="service-scheduler-date-col">
                        <label class="service-scheduler-label">Select Date</label>
                        <input type="text" class="service-scheduler-date-input" placeholder="Select a date" readonly>
                    </div>
                    <div class="service-scheduler-time-col">
                        <label class="service-scheduler-label">Select Time</label>
                        <select class="service-scheduler-time-select" disabled>
                            <option value="">-- Select date first --</option>
                        </select>
                    </div>
                </div>
            </div>
        `;

        const fieldElement = container.querySelector('.nf-field-element');
        if (fieldElement) {
            fieldElement.appendChild(wrapper);
        } else {
            container.appendChild(wrapper);
        }

        const dateInput = wrapper.querySelector('.service-scheduler-date-input');
        const timeSelect = wrapper.querySelector('.service-scheduler-time-select');

        // Initialize Flatpickr on date input
        const fp = flatpickr(dateInput, {
            dateFormat: 'm/d/Y',
            minDate: 'today',
            disableMobile: true,
            disable: [isDateDisabled],
            onChange: function(selectedDates) {
                if (selectedDates.length === 0) {
                    timeSelect.innerHTML = '<option value="">-- Select date first --</option>';
                    timeSelect.disabled = true;
                    updateOriginalInput(originalInput, '', '');
                    return;
                }

                const date = selectedDates[0];
                const dayOfWeek = date.getDay();
                const slots = getTimeSlots(dayOfWeek);

                timeSelect.innerHTML = '<option value="">-- Select time --</option>';
                slots.forEach(slot => {
                    const option = document.createElement('option');
                    option.value = slot.value;
                    option.textContent = slot.label;
                    timeSelect.appendChild(option);
                });

                timeSelect.disabled = false;
                updateOriginalInput(originalInput, formatDate(date), '');
            }
        });

        // Handle time selection
        timeSelect.addEventListener('change', function() {
            const dateValue = dateInput.value;
            const timeValue = this.value;
            updateOriginalInput(originalInput, dateValue, timeValue);
        });

        return { dateInput, timeSelect, fp };
    }

    // Update the original Ninja Forms input
    function updateOriginalInput(input, date, time) {
        if (!input) return;

        let finalValue = '';
        if (date && time) {
            finalValue = `${date} ${time}`;
        } else if (date) {
            finalValue = date;
        }

        input.value = finalValue;

        // Mark field as valid by removing error classes
        const fieldWrap = input.closest('.nf-field-wrap, .field-wrap');
        if (fieldWrap) {
            fieldWrap.classList.remove('nf-error', 'nf-fail');
            const errorMsg = fieldWrap.querySelector('.nf-error-msg');
            if (errorMsg) errorMsg.style.display = 'none';
        }

        // Trigger change event for Ninja Forms validation
        const event = new Event('change', { bubbles: true });
        input.dispatchEvent(event);

        // Also trigger input event
        const inputEvent = new Event('input', { bubbles: true });
        input.dispatchEvent(inputEvent);
    }

    // Disable Ninja Forms date validation for our custom fields
    function disableNFDateValidation() {
        // Wait for Ninja Forms to be ready
        if (typeof jQuery !== 'undefined') {
            jQuery(document).on('nfFormReady', function() {
                // Find all service scheduler fields and disable their validation
                const schedulerContainers = document.querySelectorAll('.service-scheduler-datetime');
                schedulerContainers.forEach(function(container) {
                    const nfField = container.querySelector('.ninja-forms-field');
                    if (nfField && nfField.id) {
                        const fieldId = nfField.id.replace('nf-field-', '');
                        // Remove date type validation by changing field type perception
                        if (window.nfRadio) {
                            const fieldModel = window.nfRadio.channel('fields').request('get:field', fieldId);
                            if (fieldModel) {
                                // Change type to textbox to avoid date validation
                                fieldModel.set('type', 'textbox');
                                fieldModel.set('mask', '');
                            }
                        }
                    }
                });
            });
        }

        // Also override validation on submit using MutationObserver for error messages
        const observer = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                if (mutation.target.classList && mutation.target.classList.contains('nf-error-msg')) {
                    const container = mutation.target.closest('.service-scheduler-datetime');
                    if (container) {
                        // Hide the error message for our scheduler
                        mutation.target.style.display = 'none';
                        const fieldWrap = mutation.target.closest('.nf-field-wrap');
                        if (fieldWrap) {
                            fieldWrap.classList.remove('nf-error', 'nf-fail');
                        }
                    }
                }
            });
        });

        observer.observe(document.body, {
            attributes: true,
            subtree: true,
            attributeFilter: ['class', 'style']
        });
    }

    // Initialize scheduler on a container
    function initScheduler(container) {
        if (container.dataset.schedulerInitialized) return;
        container.dataset.schedulerInitialized = 'true';

        // Find the original input (could be text input or hidden)
        let originalInput = container.querySelector('input[type="text"].ninja-forms-field');
        if (!originalInput) {
            originalInput = container.querySelector('input.ninja-forms-field');
        }
        if (!originalInput) {
            originalInput = container.querySelector('input');
        }

        createSchedulerUI(container, originalInput);
    }

    // Find and initialize all scheduler containers
    function initAllSchedulers() {
        // Look for containers with the class
        const containers = document.querySelectorAll('.service-scheduler-datetime');
        containers.forEach(container => {
            // If the class is on the input, get the parent container
            if (container.tagName === 'INPUT') {
                const fieldContainer = container.closest('.nf-field-container, .field-wrap');
                if (fieldContainer) {
                    initScheduler(fieldContainer);
                }
            } else {
                initScheduler(container);
            }
        });

        // Also check for date fields inside service-scheduler-datetime containers
        const dateContainers = document.querySelectorAll('.service-scheduler-datetime .date-container, .service-scheduler-datetime.date-container');
        dateContainers.forEach(container => {
            const fieldContainer = container.closest('.nf-field-container, .field-wrap');
            if (fieldContainer && !fieldContainer.dataset.schedulerInitialized) {
                initScheduler(fieldContainer);
            }
        });
    }

    // Main initialization
    function init() {
        loadFlatpickr(function() {
            // Add custom styles
            addStyles();

            // Disable NF date validation for custom fields
            disableNFDateValidation();

            // Initialize existing schedulers
            initAllSchedulers();

            // Watch for dynamically added forms
            const observer = new MutationObserver(function(mutations) {
                let shouldInit = false;
                mutations.forEach(function(mutation) {
                    if (mutation.addedNodes.length) {
                        mutation.addedNodes.forEach(node => {
                            if (node.nodeType === 1) {
                                if (node.classList && node.classList.contains('service-scheduler-datetime')) {
                                    shouldInit = true;
                                } else if (node.querySelector && node.querySelector('.service-scheduler-datetime')) {
                                    shouldInit = true;
                                }
                            }
                        });
                    }
                });
                if (shouldInit) {
                    setTimeout(initAllSchedulers, 100);
                }
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        });
    }

    // Add component styles
    function addStyles() {
        if (document.getElementById('service-scheduler-styles')) return;

        const style = document.createElement('style');
        style.id = 'service-scheduler-styles';
        style.textContent = `
            .service-scheduler-wrapper {
                width: 100%;
            }

            .service-scheduler-container {
                width: 100%;
            }

            .service-scheduler-row {
                display: flex;
                gap: 15px;
                flex-wrap: wrap;
            }

            .service-scheduler-date-col,
            .service-scheduler-time-col {
                flex: 1;
                min-width: 200px;
            }

            .service-scheduler-label {
                display: none !important;
            }

            .service-scheduler-date-input,
            .service-scheduler-time-select {
                width: 100%;
                height: 40px !important;
                padding: 8px 1.8rem;
                font-size: 14px;
                border: none;
                border-bottom: 1px solid #1a1a1a;
                border-radius: 0;
                background-color: #fff;
                cursor: pointer;
                box-sizing: border-box;
                text-align: center;
                color: #1a1a1a;
                -webkit-appearance: none;
                -moz-appearance: none;
                appearance: none;
            }

            .service-scheduler-time-select {
                background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='13' height='9' fill='%231a1a1a'><path d='m.066.14 6 7.894 6-7.894h-2l-4 5.427-4-5.427h-2Z'/></svg>");
                background-repeat: no-repeat;
                background-position: calc(100% - 10px) center;
                padding-right: 30px;
            }

            .service-scheduler-date-input::placeholder,
            .service-scheduler-time-select option[value=""] {
                color: #1a1a1a;
            }

            .service-scheduler-date-input:focus,
            .service-scheduler-time-select:focus {
                outline: none;
                border-bottom-color: #1a1a1a;
            }

            .service-scheduler-time-select:disabled {
                background-color: #f5f5f5;
                cursor: not-allowed;
                color: #999;
                border-bottom-color: #ccc;
            }

            .service-scheduler-info {
                margin-top: 8px;
                font-size: 11px;
                color: #666;
                text-align: center;
            }

            /* Hide Ninja Forms native date/time elements */
            .service-scheduler-datetime .pikaday__container,
            .service-scheduler-datetime .pikaday__display,
            .service-scheduler-datetime .pika-single,
            .service-scheduler-datetime .hour,
            .service-scheduler-datetime .minute,
            .service-scheduler-datetime .ampm,
            .service-scheduler-datetime > .nf-field-element > input:not(.service-scheduler-date-input),
            .service-scheduler-datetime > .nf-field-element > select:not(.service-scheduler-time-select) {
                display: none !important;
            }

            /* Flatpickr customizations */
            .flatpickr-calendar {
                font-family: inherit;
                border-radius: 8px;
                box-shadow: 0 4px 20px rgba(0,0,0,0.15);
                border: none;
            }

            .flatpickr-months {
                background: var(--primary-color, #007bff);
                border-radius: 8px 8px 0 0;
                padding: 5px 0;
            }

            .flatpickr-months .flatpickr-month {
                color: #fff;
                fill: #fff;
            }

            .flatpickr-current-month {
                color: #fff;
                font-weight: 600;
            }

            .flatpickr-current-month input.cur-year {
                color: #fff;
            }

            .flatpickr-months .flatpickr-prev-month,
            .flatpickr-months .flatpickr-next-month {
                fill: #fff;
            }

            .flatpickr-months .flatpickr-prev-month:hover,
            .flatpickr-months .flatpickr-next-month:hover {
                fill: rgba(255,255,255,0.7);
            }

            .flatpickr-months .flatpickr-prev-month svg,
            .flatpickr-months .flatpickr-next-month svg {
                fill: inherit;
            }

            .flatpickr-weekdays {
                background: #f8f9fa;
            }

            .flatpickr-weekday {
                color: #333;
                font-weight: 600;
                font-size: 11px;
            }

            .flatpickr-day {
                color: #333;
                border-radius: 4px;
            }

            .flatpickr-day:hover {
                background: #e9ecef;
                border-color: #e9ecef;
            }

            .flatpickr-day.today {
                border-color: var(--primary-color, #007bff);
            }

            .flatpickr-day.selected {
                background: var(--primary-color, #007bff) !important;
                border-color: var(--primary-color, #007bff) !important;
                color: #fff !important;
            }

            .flatpickr-day.flatpickr-disabled {
                color: #ccc;
                text-decoration: line-through;
            }

            .flatpickr-day.flatpickr-disabled:hover {
                background: transparent;
                border-color: transparent;
                cursor: not-allowed;
            }

            @media (max-width: 480px) {
                .service-scheduler-row {
                    flex-direction: column;
                }

                .service-scheduler-date-col,
                .service-scheduler-time-col {
                    min-width: 100%;
                }
            }
        `;
        document.head.appendChild(style);
    }

    // Run when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Also run on Ninja Forms ready event
    if (typeof jQuery !== 'undefined') {
        jQuery(document).on('nfFormReady', function() {
            setTimeout(initAllSchedulers, 200);
        });
    }
})();
